import { BaseQueryParams } from '../../Global.model';
import { twApi } from '../api';
import {
  ICreateDivisionData,
  IDivisionQBOClassesResponse,
  IDivisionQBOClassTrackingResponse,
  IDivisionsResponse,
  ISearchDivisionsParams,
  IUpdateDivisionData
} from './divisions.model';

/**
 * Api endpoints for divisions
 */

const divisionsApi = twApi.injectEndpoints({
  endpoints: (builder) => ({
    getDivisions: builder.query<{ content: IDivisionsResponse[]; total: number }, BaseQueryParams>({
      query: (arg) => ({
        url: `v3/divisions`,
        params: { ...arg }
      }),
      transformResponse: (response: { data: IDivisionsResponse[]; total: number }) => ({
        content: response.data,
        total: response.total
      }),
      providesTags: (result = { content: [], total: 0 }) =>
        result?.content
          ? [
              ...result.content.map(({ id }) => ({ type: 'Divisions' as const, id })),
              { type: 'Divisions', id: 'DIVISIONS_LIST' }
            ]
          : [{ type: 'Divisions', id: 'DIVISIONS_LIST' }]
    }),
    searchDivisions: builder.query<
      { content: IDivisionsResponse[]; total: number },
      { pageParams?: BaseQueryParams; filterParams?: ISearchDivisionsParams }
    >({
      query: (customerSearchParams) => {
        const { pageParams, filterParams } = customerSearchParams;

        return {
          url: `v3/divisions/search`,
          method: 'POST',
          body: { ...filterParams },
          params: { ...pageParams }
        };
      },
      transformResponse: (response: { data: IDivisionsResponse[]; total: number }) => ({
        content: response.data,
        total: response.total
      }),
      providesTags: (result = { content: [], total: 0 }) =>
        result?.content
          ? [
              ...result.content.map(({ id }) => ({ type: 'Customers' as const, id })),
              { type: 'Divisions', id: 'DIVISIONS_LIST' }
            ]
          : [{ type: 'Divisions', id: 'DIVISIONS_LIST' }]
    }),
    createDivision: builder.mutation<IDivisionsResponse, ICreateDivisionData>({
      query: (body) => {
        const { phone_ext, location_id, phone, name, email_address } = body;

        const params: ICreateDivisionData = {
          name: name,

          location_id: location_id || null
        };
        if (email_address) {
          params.email_address = email_address;
        }

        if (phone) {
          params.phone = phone;
        }
        if (phone_ext) {
          params.phone_ext = phone_ext;
        }

        return {
          url: 'v3/divisions',
          method: 'POST',
          body: {
            ...params
          }
        };
      },
      transformResponse: (response: { data: IDivisionsResponse; message: string }) => response.data,
      invalidatesTags: [{ type: 'Divisions', id: 'DIVISIONS_LIST' }]
    }),
    getDivisionById: builder.query<IDivisionsResponse, string>({
      query: (id) => ({
        url: `v3/divisions/${id}`
      }),
      transformResponse: (response: { data: IDivisionsResponse }) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: 'Divisions' as const, id }]
    }),
    updateDivision: builder.mutation<IDivisionsResponse, IUpdateDivisionData>({
      query: (divisionData: IUpdateDivisionData) => {
        const { id, name, email_address, phone, phone_ext, location_id } = divisionData;

        const params: Omit<IUpdateDivisionData, 'id'> = {
          name: name,
          email_address: !!email_address ? email_address : null,
          phone: !!phone ? phone : null,
          phone_ext: !!phone_ext ? phone_ext : null,
          location_id: !!location_id ? location_id : null
        };

        return {
          url: `v3/divisions/${id}`,
          method: 'PUT',
          body: { ...params }
        };
      },
      transformResponse: (response: { data: IDivisionsResponse; message: string }) => response.data,
      invalidatesTags: (result, error, data) => [
        { type: 'Divisions', id: data.id },
        { type: 'Divisions', id: 'DIVISIONS_LIST' }
      ]
    }),
    deleteDivision: builder.mutation({
      query: (id: string) => ({
        url: `v3/divisions/${id}`,
        method: 'DELETE'
      }),
      onQueryStarted: (id, { dispatch, queryFulfilled }) => {
        const update = dispatch(
          divisionsApi.util.updateQueryData('getDivisions', {}, (divisionsData) => {
            const index = divisionsData.content.findIndex((c) => c.id === id);
            delete divisionsData.content[index];
            divisionsData.total -= 1;
          })
        );
        queryFulfilled.catch(() => {
          update.undo();
        });
      },
      invalidatesTags: (result, error, id) => [
        { type: 'Divisions', id },
        { type: 'Divisions', id: 'DIVISIONS_LIST' }
      ]
    }),
    getQBOClassesByDivision: builder.query<IDivisionQBOClassesResponse, string>({
      query: (id) => ({
        url: `v3/divisions/${id}/qbo-all-classes`
      }),
      transformResponse: (response: IDivisionQBOClassesResponse) => {
        return {
          data: response.data,
          size: response.size
        };
      },
      providesTags: (result, error, id) => [{ type: 'Divisions', id }]
    }),
    getQBOClassTracking: builder.query<IDivisionQBOClassTrackingResponse, string>({
      query: (id) => ({
        url: `v3/divisions/${id}/qbo-class-tracking`
      }),
      transformResponse: (response: { data: IDivisionQBOClassTrackingResponse }) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: 'Divisions', id }]
    })
  })
});

export const {
  useGetDivisionsQuery,
  useSearchDivisionsQuery,
  useCreateDivisionMutation,
  useGetDivisionByIdQuery,
  useUpdateDivisionMutation,
  useDeleteDivisionMutation,
  useGetQBOClassesByDivisionQuery,
  useGetQBOClassTrackingQuery
} = divisionsApi;
